import * as React from 'react';
import {isObject, isPlainObject} from 'lodash';
import {Map as IMap, List as IList, fromJS} from 'immutable';
import {
  weakMemo,
  cacheMemo,
  memoize,
  singleMemo,
} from '@packages/utilities/memo';
import {append} from '@packages/utilities/array';
import {emptyArray, emptyObject, concat} from '@packages/utilities/fp/utils';
import {Pair, pair, getFirst, getSecond} from '@packages/utilities/Pair';
import {
  Component,
  Types,
  Type,
  Expressions,
  Plugins,
  EvalEnv,
  Variable,
  EnvironmentContextType,
  PluginComponentContextType,
  PluginsContext,
  ContextType,
  NormalizedAtomConfig,
  AttributesMap,
  ClassesList,
  getMeta,
  TypeImplementation,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  qualify,
  Qualified,
  QualifiedMap,
  identifier,
  getPluginName,
  getName,
  namesEqual,
  toHumanReadableString,
} from '@packages/systems/wfdl-Qualified';
import {
  pageComponentType,
  nativeIdPathsEqual,
  getCurrentComponentType,
} from '@packages/utilities/platform';
import {
  isStringElement,
  isFigCaption,
} from '@packages/utilities/elementTypeUtils';
import {getDataTypeFromElement} from '@packages/systems/user-components/utils';
import {
  SharedContextType,
  SharedContext,
} from '@packages/systems/core/records/SharedContext';
import {
  NativeId,
  updateInstanceIndexSuffix,
  computeNativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  generateNamespace,
  generateNamespaceWithVariantKey,
} from '@packages/systems/style/engine';
import {
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
} from '@packages/systems/style/breakpoint-ids';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {
  PLUGIN_COMPONENT_TYPE,
  SYMBOL_COMPONENT_TYPE,
} from '@packages/systems/core/utils/SeenComponentsUtils';
import {
  createGenerateAttributes,
  mergeStyleAttributes,
} from './AttributesAndClasses';
import {nodePojoToElementWithComponents} from '@packages/systems/core/component-conversions';
import {getTopLevelPropBindings} from '@packages/systems/dynamo/wfdl';
import {mut_copyBindingProps} from '@packages/systems/dynamo/binding-props';
import {useDynamoProps} from '@packages/systems/dynamo/useDynamoProps';
import {CONDITION_INVISIBLE_CLASS} from '@packages/systems/dynamo/constants';
import {
  Expansion,
  ExpansionProps,
  EvalElementProps,
  EvaluatedData,
} from './types';
import {Env, Ix3Targets, freshEnvForReifiedSubtree} from './Env';
import {transformDataEnv, transformExpansionProps} from './transforms';
import {
  RenderContextProvider,
  RenderContext,
  defaultRenderContext,
  usePageId,
  useStyleBlockAttributes,
  useCollectionIdToPageIdMap,
  useRuntimeEnvironment,
  usePreviewMode,
  emptyActiveVariants,
  useVariantState,
  useSlugMap,
  useCssVariableCollectionsFromRenderContext,
  useIx3ElementTargets,
  useOmitHiddenElements,
} from './RenderContext';
import {componentEqualProps, commonEqualProps} from './propsEqualUtils';
import {
  parseVariantStateKey,
  stringifyVariantStateKey,
  VariantStateKey,
} from '@packages/systems/style-legacy/selectors';
import {FIGCAPTION_PLACEHOLDER_TEXT} from '@packages/shared/constants/Render';
import * as KeySet from '@packages/utilities/key-set';
import {
  createComponentVariantsAttributeMap,
  getStyleVariantTypeArguments,
} from '@packages/systems/component-style-variants';
import {ActiveVariants} from '@packages/systems/style/types';
import {RenderTrackingContextItem} from '@packages/systems/element-tracking-types';
import {
  isVisible,
  memoIsVisible,
  isKeptInHtmlWhenHidden,
} from './utils/visibility';
import {
  COMPONENT_ID_ATTR,
  VARIANT_STATE_ATTR,
  elementTargetAttribute,
  GetIx3ElementTargetsReturn,
  getComponentRootIdForAttribute,
  getIx3ElementTargets,
} from '@packages/systems/ix3/shared';
import {pruneElementDataToStyleVariants} from './utils/pruneElementDataToStyleVariants';
import {EvaluatedNodeData} from '@packages/utilities/data-utils';
import {useDynamoRichTextToRichTextChildren} from './useDynamoRichTextToRichTextChildren';
import {resolveVariableModeAttributes} from '@packages/systems/variable-mode-props';
import {defer} from '@packages/utilities/lazy';
import {isNodePojo} from '@packages/systems/core/nodepojo';

function isLocalStylesObject(
  obj: unknown
): obj is {base?: unknown; variants?: Record<string, unknown>} {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    ('base' in obj || 'variants' in obj)
  );
}

// The renderer determines how each field was bound and passes it down
// as `fieldBindings` (see AtomRenderProps).
//
// It's wrapped in `defer` and memoized per element, so the work only runs if an
// atom actually reads it (only Image does today), and the thunk stays a stable
// reference across renders.
const getTopLevelPropBindingsLazy = weakMemo((elementData: Expression) =>
  defer(() => getTopLevelPropBindings(elementData))
);

const mergeComponentContextToEnv = weakMemo(function _mergeComponentContext(
  context: PluginComponentContextType
) {
  return weakMemo(function _mergeEnv(env: EvalEnv.EvalEnvType) {
    return EvalEnv.insertValue(
      env,
      Variable.unqualified(identifier('context')),
      context
    );
  });
});

// Memoize the style check for each object reference
const hasNonEmptyLocalStyles = weakMemo((style: any): boolean => {
  if (!style) return false;

  // Check base styles
  if (style.base) {
    const breakpoints = Object.values(style.base);
    const bpLength = breakpoints.length;
    for (let i = 0; i < bpLength; i++) {
      const breakpoint = breakpoints[i];
      if (!breakpoint) continue;

      const pseudoStates = Object.values(breakpoint);
      const psLength = pseudoStates.length;
      for (let j = 0; j < psLength; j++) {
        const styles = pseudoStates[j];
        if (styles && Object.keys(styles).length > 0) {
          return true;
        }
      }
    }
  }

  // Check variant styles
  if (style.variants) {
    const variants = Object.values(style.variants);
    const varLength = variants.length;
    for (let i = 0; i < varLength; i++) {
      const variant = variants[i];
      if (!variant) continue;

      const breakpoints = Object.values(variant);
      const bpLength = breakpoints.length;
      for (let j = 0; j < bpLength; j++) {
        const breakpoint = breakpoints[j];
        if (!breakpoint) continue;

        const pseudoStates = Object.values(breakpoint);
        const psLength = pseudoStates.length;
        for (let k = 0; k < psLength; k++) {
          const styles = pseudoStates[k];
          if (styles && Object.keys(styles).length > 0) {
            return true;
          }
        }
      }
    }
  }

  return false;
});

const isComponentInstanceInFocusedComponentPath = (
  environmentContext: EnvironmentContextType,
  nativeIdPath: Array<NativeId>
) => {
  const focusedSymbolNativeIdPath = environmentContext.get(
    'focusedSymbolNativeIdPath'
  );

  return (
    focusedSymbolNativeIdPath &&
    nativeIdPathsEqual(
      nativeIdPath,
      focusedSymbolNativeIdPath.slice(0, nativeIdPath.length)
    )
  );
};

const REACT_ELEMENT_TYPE: symbol = Symbol.for('react.element');

const isIndexableObject = (obj: unknown): obj is {[key: string]: unknown} =>
  obj ? typeof obj === 'object' : false;

const getDataChildren = (data: EvaluatedData): null | Array<EvaluatedData> => {
  if (data && typeof data === 'object' && 'children' in data) {
    const children = data.children;
    return Array.isArray(children) ? children : null;
  }
  return null;
};

const dataVar = Variable.unqualified(identifier('data'));

const mergeDataToEnv = weakMemo((env: EvalEnv.EvalEnvType) => {
  const merge = (data: EvaluatedData) =>
    EvalEnv.insertValue(env, dataVar, data);
  const memoMerge = weakMemo(merge);
  return (data: EvaluatedData) =>
    isObject(data) ? memoMerge(data) : merge(data);
});

const FORBIDDEN_ATTRIBUTES = ['dangerouslySetInnerHTML', 'is'];

/**
 * Based on a plugin local context (e.g. {round: true}), adds that to the existing context of that plugin
 * and returns the new plugins context
 * The plugins context is an Immutable Map that contains a key for each plugin, e.g.:
 * {
 *   slider: {round: true},
 *   symbol: {instance: '4554-3545-3545-3455'},
 *   tabs: {current: 1},
 * }
 * @param  {Object}  props
 * @param  {Immutable.Map<String:Any>} pluginContext   (Optional) An Immutable Map that contains some context that
 *                                                     the atom can pass to the children of the same plugin
 * @param  {Number}                    index           (Optional) The index of the current child for whom the context is
 *                                                     being generated
 * @return {Immutable.Map<String:Any>}                 The updated full context for this atom
 */

const memoizeWithDepth = cacheMemo(100);

const generateContextWithPluginContext = weakMemo(
  (pluginsContext: PluginsContext) =>
    weakMemo((pluginContext: ContextType) =>
      memoizeWithDepth((index: number) =>
        memoizeWithDepth((pluginKey: string): PluginsContext => {
          // Merge the existing plugin context with whatever context has been passed to `renderChildn`
          // For example: if the previous 'slider' `existingPluginContext` was {round: true} and `newPluginContext` is
          // {arrows: false}, `pluginContext` will be {slider: {round: true, arrows: false}}
          const updatedPluginContext = pluginsContext
            .get(pluginKey, IMap())
            .merge(pluginContext)
            .set('index', index);

          return pluginsContext.set(pluginKey, updatedPluginContext);
        })
      )
    )
);

const generateContextWithoutPluginContext = weakMemo(
  (pluginsContext: PluginsContext) =>
    memoizeWithDepth((pluginKey: string): PluginsContext => {
      // Make sure that also plugins that don't have initial context get context.
      return pluginsContext.get(pluginKey)
        ? pluginsContext
        : pluginsContext.set(pluginKey, IMap());
    })
);

const generateContext = (
  pluginKey: string,
  pluginsContext: PluginsContext,
  pluginContext?: ContextType,
  index?: number
): PluginsContext => {
  index = index ? index : 0;
  if (pluginContext) {
    return generateContextWithPluginContext(pluginsContext)(pluginContext)(
      index
    )(pluginKey);
  } else {
    return generateContextWithoutPluginContext(pluginsContext)(pluginKey);
  }
};

const getLastSeenComponentTypeMemo = weakMemo(
  (currentComponent: Qualified<Component.ComponentName>) => {
    const inPage = namesEqual(currentComponent, pageComponentType);
    return inPage
      ? emptyObject
      : {
          [getName(currentComponent)]:
            getPluginName(currentComponent) === '__SitePlugin'
              ? SYMBOL_COMPONENT_TYPE
              : PLUGIN_COMPONENT_TYPE,
        };
  }
);

// `variantState` is injected into `environmentContext` at render time. A bare
// `setIn` allocates a new map on every `Render` render (the base map from the
// Designer doesn't already carry the value), which breaks the
// `environmentContext === ` bail in `commonEqualProps` and re-renders every atom
// whenever `Render` re-renders — e.g. on a breakpoint change, since the active
// breakpoint now lives on `renderContext`.
const setEnvironmentContextVariantState = weakMemo(
  (environmentContext: EnvironmentContextType) =>
    singleMemo((variantState: VariantStateKey | null) =>
      environmentContext.setIn(['variantState'], variantState)
    )
);

const getLastSeenComponentType = (
  currentComponent: null | Qualified<Component.ComponentName>
) =>
  currentComponent
    ? getLastSeenComponentTypeMemo(currentComponent)
    : emptyObject;

const useDynamo = (
  originalElement: ExpressionElement,
  env: Env,
  atom: NormalizedAtomConfig
) => {
  const lastSeenComponentType = getLastSeenComponentType(env.currentComponent);

  const {
    evalElementProps: {
      originalElement: dynamoElement,
      environmentContext,
      sharedContext,
      plugins,
      components,
      componentContext,
      nativeIdPath,
      isComponentRootElement,
      trackingContext,
    },
    env: evalEnv,
    pluginsContext,
    dynamoAttributes,
    dynamoClasses,
    dynamoBindingProps,
    legacyDynamoBindingPaths,
  } = useDynamoProps(
    {
      env: env.evalEnv,
      plugins: env.plugins,
      components: env.components,
      isComponentRootElement: env.isComponentRootElement,
      originalElement,
      componentContext: env.componentContext,
      environmentContext: env.environmentContext,
      sharedContext: env.sharedContext,
      pluginsContext: env.pluginsContext,
      nativeIdPath: env.nativeIdPath,
      seenComponents: lastSeenComponentType,
      trackingContext: env.trackingContext,
    },
    atom
  );

  // Dynamo can give us an element from a previous render so we need to use that
  const element = dynamoElement;

  return {
    element,
    environmentContext,
    sharedContext,
    plugins,
    components,
    componentContext,
    nativeIdPath,
    isComponentRootElement,
    evalEnv,
    pluginsContext,
    dynamoAttributes,
    dynamoClasses,
    dynamoBindingProps,
    legacyDynamoBindingPaths,
    trackingContext,
  };
};

export const createBaseAttributeMap = (
  componentId: null | string,
  elementId: string,
  data: EvaluatedData,
  activeVariants: ActiveVariants,
  ix3PotentialTargets: Ix3Targets,
  nativeIdPath: Array<string>,
  ix3ElementTargets: GetIx3ElementTargetsReturn
): AttributesMap => {
  if (!data || typeof data !== 'object') return IMap();

  return IMap<string, string>().withMutations((attrMap) => {
    // Get the attributes.
    const attr = 'attr' in data ? data.attr : null;
    if (isIndexableObject(attr)) {
      // @ts-expect-error - there is no guarantee that `attr` contains string values
      attrMap.merge(attr);

      // React does not render boolean values for custom attributes so we need to
      // convert them to strings ourselves.
      if (attr.autofocus === true) {
        attrMap.set('autofocus', 'true');
      }
    }

    // Get the custom attributes list.
    const xattr = 'xattr' in data ? data.xattr : null;
    if (Array.isArray(xattr)) {
      for (const item of xattr) {
        if (
          item &&
          typeof item === 'object' &&
          'name' in item &&
          'value' in item
        ) {
          attrMap.set(item.name, item.value);
        }
      }
    }

    // Remove forbidden attributes.
    for (const forbiddenAttribute of FORBIDDEN_ATTRIBUTES) {
      if (attrMap.has(forbiddenAttribute)) {
        attrMap.remove(forbiddenAttribute);
      }
    }

    if (attrMap.get('id') === '') {
      attrMap.remove('id');
    }

    // Remove class, as it is now handled in createBaseClassList
    attrMap
      .keySeq()
      .toArray() // Convert to array to avoid modifying the map while iterating.
      .forEach((key: string) => {
        if (key.toLowerCase().trim() === 'class') {
          attrMap.remove(key);
        }
      });

    const ix3ReifiedTargets = getIx3ElementTargets(ix3ElementTargets);

    const initIx3Targets: Array<
      Pair<Pair<Component.ComponentName, string>, Array<string>>
    > =
      componentId !== null && ix3ReifiedTargets[componentId]?.[elementId]
        ? [pair(pair(componentId, elementId), nativeIdPath)]
        : emptyArray;

    const ix3Targets = concat(
      ix3PotentialTargets.filter((conf) => {
        const elId = getFirst(conf);
        return ix3ReifiedTargets[getFirst(elId)]?.[getSecond(elId)];
      })
    )(initIx3Targets);

    if (ix3Targets.length > 0) {
      const targetAttr = elementTargetAttribute(ix3Targets);
      attrMap.set(getFirst(targetAttr), getSecond(targetAttr));
    }

    const style = 'style' in data ? data.style : null;
    if (!style) return;

    // HACK:
    // The `data.style` key is currently used by both "local styles" (e.g. grid)
    // and "element inline styles" (e.g. `padding-bottom` for Figure).
    // This check avoids inlining local styles, while keeping backwards compatibility with existing cases.
    // Github issue for discussion: https://github.com/webflow/webflow/issues/20492
    if (isLocalStylesObject(style)) {
      const variantsKeys = Object.keys(style.variants ?? emptyObject);
      const hasActiveLocalStyles =
        style.base != null ||
        // determine if any element local styles match an active variant from component scope
        variantsKeys.some((key) => KeySet.has(activeVariants, key));

      // TODO: Check if we need to generateNamespace if nodeStyles are inline styles.
      // Create a namespace for the id attribute if we have local styles but don't
      // have a user provided "id" attribute.
      // Note: empty-string `componentId` is valid here (e.g. LLM render with no
      // pageId); `generateNamespace` handles it. Only `null` should skip.
      if (
        componentId !== null &&
        hasActiveLocalStyles &&
        hasNonEmptyLocalStyles(style) &&
        !attrMap.has('id')
      ) {
        attrMap.set('id', generateNamespace(elementId, componentId));
      }
    }
    // If the nodeStyles are Atom instance inline styles, we need to update the
    // attribute Map to include these inline styles.
    else if (Object.keys(style).length > 0) {
      attrMap.set('style', fromJS(style));
    }
  });
};

export const createBaseClassList = (
  componentId: null | string,
  element: ExpressionElement,
  data: EvaluatedData,
  styleBlockClasses: string,
  activeVariants: ActiveVariants
): ClassesList =>
  IList<string>().withMutations((mut_classList) => {
    if (styleBlockClasses) mut_classList.push(styleBlockClasses);

    if (data && typeof data === 'object') {
      // We need to create a namespace class for local styles if the element
      // has a custom "id" attribute. This is to allow Webflow local styles
      // to have a higher specifity than any styles a user might have specified
      // via custom code using an "id" selector.

      if (`style` in data) {
        if (
          componentId !== null &&
          'attr' in data &&
          data.attr &&
          typeof data.attr === 'object' &&
          `id` in data.attr &&
          data.attr.id
        ) {
          mut_classList.push(generateNamespace(element.id, componentId));
        }

        const style = data.style;
        const hasVariants =
          style && typeof style === 'object' && 'variants' in style;

        if (hasVariants) {
          // Get all local style variant keys that are present in `activeVariants`
          const activeVariantKeys =
            hasVariants && typeof style.variants === 'object'
              ? Object.keys(style.variants ?? {}).filter((key) =>
                  KeySet.has(activeVariants, key)
                )
              : undefined;

          if (activeVariantKeys) {
            // For each active local style variant key, add a namespaced class that
            // includes the variant key as part of the class name.
            // This is done to allow variant local styles to be applied on top of base
            // local style rules without replacing them completely.
            for (const variantKey of activeVariantKeys) {
              mut_classList.push(
                generateNamespaceWithVariantKey(variantKey as VariantStateKey)
              );
            }
          }
        }
      }

      // Add visibility classes
      const vis = 'vis' in data ? data.vis : null;
      if (vis && typeof vis === 'object') {
        if (BREAKPOINT_ID_MAIN in vis && vis[BREAKPOINT_ID_MAIN] === false)
          mut_classList.push('w-hidden-main');
        if (BREAKPOINT_ID_MEDIUM in vis && vis[BREAKPOINT_ID_MEDIUM] === false)
          mut_classList.push('w-hidden-medium');
        if (BREAKPOINT_ID_SMALL in vis && vis[BREAKPOINT_ID_SMALL] === false)
          mut_classList.push('w-hidden-small');
        if (BREAKPOINT_ID_TINY in vis && vis[BREAKPOINT_ID_TINY] === false)
          mut_classList.push('w-hidden-tiny');
      }

      if (!memoIsVisible(data)) {
        mut_classList.push(CONDITION_INVISIBLE_CLASS);
      }

      // Add block classes
      const block = 'block' in data ? data.block : null;
      if (block === 'inline') mut_classList.push('w-inline-block');
      if (block === true) mut_classList.push('w-inline-block');

      // Add clearfix class
      if ('clearfix' in data && data.clearfix) mut_classList.push('w-clearfix');

      // Add password page class
      if (
        'form' in data &&
        data.form &&
        typeof data.form === 'object' &&
        'passwordPage' in data.form &&
        data.form.passwordPage
      )
        mut_classList.push('w-password-page');

      // Add Figcaption placeholder class
      if (isFigCaption(element)) {
        const children = getDataChildren(data);
        if (children) {
          if (children.some((item) => item === FIGCAPTION_PLACEHOLDER_TEXT))
            mut_classList.push('w-richtext-figcaption-placeholder');
        }
      }
    }
  });

// Formats an incoming prop value according to the meta and formatValue API
// defined in its fields TypeImplementation
const formatOverrideValue = (
  plugins: Plugins.PluginsType,
  type: Type,
  data: EvaluatedData
) => {
  const typeImpl = Types.getImplementation(type, plugins);
  const meta = getMeta(type);
  // If we have meta, and the CustomTypeImplementation has a formatValue fn
  if (
    meta &&
    typeImpl &&
    typeImpl instanceof TypeImplementation.CustomTypeImplementation &&
    typeImpl.formatValue
  ) {
    return typeImpl.formatValue(meta, data);
  }
  return data;
};

const evaluateComponentDefaults = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((componentContext: PluginComponentContextType) =>
    weakMemo(
      (defaultData: Expression): Record<string, EvaluatedData> | null => {
        const fn = Expressions.evaluate(
          Plugins.getEvalEnv(plugins),
          defaultData
        );
        if (typeof fn !== 'function') return null;

        return fn(componentContext) as Record<string, EvaluatedData>;
      }
    )
  )
);

const handleOverrides = (
  plugins: Plugins.PluginsType,
  data: EvaluatedData,
  dataType: Type,
  evaluatedDefaults: Record<string, EvaluatedData> | null,
  components: Component.ComponentMap
) => {
  if (!isIndexableObject(data)) return data;
  const overridesFields = Types.getRecordFields(dataType);
  if (!overridesFields) return data;

  let updatedData = data;
  let didChangeData = false;

  // Fill in data with default values and remove properties that don’t typecheck
  Object.entries(overridesFields).forEach(([key, type]) => {
    const defaultValue: EvaluatedData =
      evaluatedDefaults &&
      Object.prototype.hasOwnProperty.call(evaluatedDefaults, key)
        ? evaluatedDefaults[key]
        : Types.getTypeDefaultValue(plugins)(type);

    if (updatedData.hasOwnProperty(key)) {
      const formattedValue = formatOverrideValue(
        plugins,
        type,
        updatedData[key]
      );
      if (formattedValue !== updatedData[key]) {
        if (!didChangeData) {
          didChangeData = true;
          updatedData = {...data};
        }
        updatedData[key] = formattedValue;
      }

      const currentValue = updatedData[key];
      const isRecord = Types.getRecordFields(type);

      if (
        isRecord &&
        typeof defaultValue === 'object' &&
        isPlainObject(defaultValue) &&
        typeof currentValue === 'object'
      ) {
        if (!didChangeData) {
          didChangeData = true;
          updatedData = {...data};
        }
        updatedData[key] = {...defaultValue, ...currentValue};
      }
      // Style Variant override can reference a non-existing variant
      // In these cases, we should use the default value
      if (Types.isStyleVariantType(type) && currentValue !== defaultValue) {
        const {variantFieldId, componentName, pluginName} =
          getStyleVariantTypeArguments(type);
        const component =
          componentName && pluginName
            ? components.get(qualify(pluginName, componentName))
            : null;
        const variantOptions = variantFieldId
          ? component?.variants?.[variantFieldId]
          : null;
        if (
          variantOptions &&
          !variantOptions.some((v) => v.id === currentValue)
        ) {
          if (!didChangeData) {
            didChangeData = true;
            updatedData = {...data};
          }
          updatedData[key] = defaultValue;
        }
      }
      return;
    }

    if (!didChangeData) {
      didChangeData = true;
      updatedData = {...data};
    }
    updatedData[key] = defaultValue;
  });

  return updatedData;
};

const blockElementType = qualify(
  Plugins.name('Basic'),
  Component.name('Block')
);

const symbolElementType = qualify(
  Plugins.name('Symbol'),
  Component.name('Symbol')
);

/**
 * WeakMemo'd wrapper around `Object.keys`
 */
const memoObjectKeys = weakMemo(
  <T extends Record<string, unknown>>(obj: T): string[] => Object.keys(obj)
);

/**
 * Computes a set of active `VariantStateKey`s based on the current component's
 * evaluation environment data and the available variants on the component
 * definition.
 *
 * This set of `VariantStateKey`s describes all Component Style Variants that
 * are currently applied, within the context of the currently-rendered component
 * render body, based on the evaluated data from the component instance.
 */

const useActiveVariants = (
  components: Component.ComponentMap,
  currentlyEvaluatedComponent: null | Qualified<Component.ComponentName>,
  evalEnv: EvalEnv.EvalEnvType,
  environmentContext: EnvironmentContextType,
  nativeIdPath: string[]
): ActiveVariants => {
  const variantState = useVariantState();
  const componentDefinition = currentlyEvaluatedComponent
    ? components.get(currentlyEvaluatedComponent)
    : undefined;
  const localData = evalEnv.values.local.data;
  const variantFieldIds = componentDefinition?.variants
    ? memoObjectKeys(componentDefinition.variants)
    : emptyArray;

  const focusedSymbolNativeIdPath = environmentContext.get(
    'focusedSymbolNativeIdPath'
  );
  const focusedComponentType = focusedSymbolNativeIdPath
    ? getCurrentComponentType(components, focusedSymbolNativeIdPath)
    : null;

  const isComponentInstanceInFocusedPath =
    isComponentInstanceInFocusedComponentPath(environmentContext, nativeIdPath);
  // We are evaluating the currently focused component if the instance is in focused path
  // and the focused component type matches the currently evaluated component
  const isEvaluatingFocusedComponent =
    isComponentInstanceInFocusedPath &&
    focusedComponentType === currentlyEvaluatedComponent;

  return React.useMemo(() => {
    if (variantFieldIds.length === 0) {
      return emptyActiveVariants;
    }

    // Always return `variantState` from `RenderContext` as the active variant when rendering elements
    // within the current focused component instance to ensure applied variant styles match the
    // selected variant state when editing components.
    // Only use the information from variantState for the outer most focused component
    if (isEvaluatingFocusedComponent) {
      // If there's no `variantState` set, base styles should be applied
      if (!variantState) return emptyActiveVariants;

      const [currentVariantFieldId] = parseVariantStateKey(variantState);

      // Make sure the active `variantState` includes a key from `component.variants`,
      // otherwise return an empty active variant state
      return variantFieldIds.includes(currentVariantFieldId)
        ? {[variantState]: true}
        : emptyObject;
    }

    const activeVariantKeys = variantFieldIds
      .filter((fieldId) => localData[fieldId] != null)
      .map((fieldId) => stringifyVariantStateKey(fieldId, localData[fieldId]));

    return KeySet.fromArray(activeVariantKeys);
  }, [variantFieldIds, localData, variantState, isEvaluatingFocusedComponent]);
};

function getActiveVariantOptionId(activeVariants: ActiveVariants): string {
  const keys = Object.keys(activeVariants);
  if (keys.length === 0) return 'base';
  // Components currently support a single variant field. If multi-field is
  // added, this needs to encode all active variant option IDs.
  const [, optionId] = parseVariantStateKey(keys[0] as VariantStateKey);
  return optionId ?? 'base';
}

export const createRender = (expansion: Expansion) => {
  const evalExpr: (expr: Expression) => (env: Env) => unknown =
    Expressions.evaluateWith(
      {
        get: (env) => env.evalEnv,
        set: (evalEnv) => (env) => ({...env, evalEnv}),
      },
      // Evaluated elements can take one of the following forms:
      // 1. `EvalElement` React element which captures the correct environment
      //    in which to further evaluate the element when it ends up being used
      // 2. Plain string which is just an optimization to not have to go through
      //    the above for the simple case when we have a String element
      // 3. `NodePojo` which is forced on us by Dynamo plugin functions
      //    and Type default values
      (el) => (env) => {
        // String performance shortcut: return a plain string directly
        if (isStringElement(el)) return evalExpr(el.data)(env);

        const component = env.components.get(el.type);

        return React.createElement(
          component ? EvalComponentElement : EvalAtomElement,
          {
            key: el.id,
            element: el,
            env,
          }
        );
      }
    );

  const evalNodePojo = (env: Env, thing: EvaluatedData) => {
    if (!isNodePojo(thing)) return thing;

    if ('text' in thing && thing.text === true) {
      if (!('data' in thing)) return thing;
      const data = thing.data;
      if (!data || typeof data !== 'object') return thing;
      if (!('value' in data)) return thing;
      return data.value;
    }

    if (!('type' in thing)) return thing;
    const nodeType = thing.type;

    if (typeof nodeType === 'string') {
      const element = nodePojoToElementWithComponents(env.plugins)(
        env.components
      )(thing);
      const component = env.components.get(element.type);

      return React.createElement(
        component ? EvalComponentElement : EvalAtomElement,
        {element, env: freshEnvForReifiedSubtree(env), key: element.id}
      );
    }

    return thing;
  };

  const renderChild =
    (atom: NormalizedAtomConfig, props: ExpansionProps) =>
    (
      child: EvaluatedData | null,
      pluginContext?: ContextType,
      key?: string,
      instanceIndex?: number,
      trackingContextItem?: RenderTrackingContextItem
    ): null | React.ReactElement<unknown> | string => {
      if (typeof child === 'string') {
        return child;
      }
      const pluginsContext = generateContext(
        atom.pluginKey,
        props.pluginsContext,
        pluginContext
      );
      const environmentContext =
        typeof instanceIndex === 'number'
          ? updateInstanceIndexSuffix(props.environmentContext)(instanceIndex)
          : props.environmentContext;

      const evaluatedChild = evalNodePojo(props.fullEnv, child);
      const childEvalElement = getEvalElement(evaluatedChild);
      if (!childEvalElement) {
        return null;
      }

      const {env} = childEvalElement.props;

      const {nativeIdPath, trackingContext} = childEvalElement.props.env
        .__HACK__isDynamoRichText
        ? {
            nativeIdPath: props.nativeIdPath,
            trackingContext: props.trackingContext,
          }
        : {
            nativeIdPath: env.nativeIdPath,
            trackingContext: env.trackingContext,
          };

      return {
        ...childEvalElement,
        key: key || childEvalElement.key,
        props: {
          ...childEvalElement.props,
          env: {
            ...env,
            pluginsContext,
            environmentContext,
            nativeIdPath,
            trackingContext,
            ...(trackingContextItem && {
              trackingContext: append(trackingContext, trackingContextItem),
            }),
          },
        },
      };
    };

  const atomProviderRender =
    (atom: NormalizedAtomConfig) => (props: ExpansionProps) => {
      const {
        attributes,
        fullEnv,
        pluginsContext,
        // Contains dynamo style overrides.
        style,
        data: originalData,
        nativeIdPath,
        classes,
        sharedContext,
        environmentContext,
        legacyDynamoBindingPaths,
      } = props;

      const AtomRender = atom.render;

      const attributesWithDynamoStyles = mergeStyleAttributes(
        attributes,
        style
      );
      const data: EvaluatedNodeData = fixAtomData(
        atom.dataType,
        originalData,
        fullEnv
      );
      const generateAttributes =
        createGenerateAttributes(atom)(data)(pluginsContext);

      const atomRenderProps: AtomRenderProps = {
        elementId: props.element.id,
        data,
        legacyDynamoBindingPaths,
        fieldBindings: getTopLevelPropBindingsLazy(props.element.data),
        nativeIdPath,
        generateAttributes,
        attributes: attributesWithDynamoStyles,
        context: pluginsContext.get(atom.pluginKey, IMap()),
        classes,
        sharedContext,
        environmentContext,
        renderChild: renderChild(atom, props),
      };

      // TODO: remove bindingProps as top-level props once generic bindings are enabled,
      // and use getAtomDataWithDataType to retrieve all bound properties and their values.
      // See https://webflow.slack.com/archives/C01EEK1ASCV/p1620745706195800 for discussion
      mut_copyBindingProps(props, atomRenderProps);

      return React.createElement(AtomRender, atomRenderProps);
    };

  const expand = weakMemo((atom: NormalizedAtomConfig) =>
    expansion(atomProviderRender(atom), atom)
  );

  const fixAtomData = (
    atomDataType: Type,
    atomData: EvaluatedData,
    env: Env
  ): EvaluatedNodeData => {
    const go = (data: EvaluatedData, type: Type): EvaluatedData => {
      if (Types.isElementType(type)) {
        return evalNodePojo(env, data);
      }

      const aliasType = Types.getAliasType(type, env.plugins);
      if (aliasType) {
        return go(data, aliasType);
      }

      const recordFields = Types.getRecordFields(type);
      if (recordFields) {
        if (!isIndexableObject(data)) return emptyObject;

        const tail = Types.getRecordTail(type);

        // Some atoms declare an open row (a `TypeReference` tail) for fields their `dataType`
        // can't enumerate up front (e.g. OptimizationBaseAtom's `experiencesByVariationId`,
        // a variationId/experienceId map). Preserve those verbatim; there's no field type
        // to recurse `go()` into for keys the dataType doesn't declare.
        const hasNoDeclaredFields = Object.keys(recordFields).length === 0;
        const isOpen =
          hasNoDeclaredFields && tail && Boolean(Types.getTypeReference(tail));

        const initialData: {[key: string]: unknown} = isOpen
          ? {...data}
          : // This is kludgy, but IX1 cheats, and tags an extra "macro" field on to whatever it wants.
            // We could add a macro field to defaultIntegratedTypes, but that would add a field to
            // everything in order to support a deprecated feature; so it seems better to keep
            // this as a temporary workaround until we can remove IX1.
            data.hasOwnProperty('macro')
            ? {macro: data.macro}
            : {};

        return Object.keys(recordFields).reduce<{[key: string]: EvaluatedData}>(
          (memo: {[key: string]: EvaluatedData}, key: string) => {
            const fieldType = recordFields[key];
            if (fieldType) {
              const value = go(data[key], fieldType);
              memo[key] = value;
            }
            return memo;
          },
          initialData
        );
      }

      const unionValues = Types.getUnionValues(type);
      if (unionValues) {
        if (!isIndexableObject(data)) return data;
        const tag = data.tag;
        if (typeof tag !== 'string') return data;
        const union = unionValues[tag];
        if (!union) return data;
        return {
          tag,
          val: go(data.val, union),
        };
      }

      const listType = Types.getListItemType(type);
      if (listType) {
        if (!Array.isArray(data)) return emptyArray;
        return data.map((listItem: EvaluatedData) => go(listItem, listType));
      }
      return data;
    };
    return go(atomData, atomDataType);
  };

  const internalEvalComponentElementFactory = memoize(
    (runtimeEnvironment: Environment) =>
      React.memo(function InternalEvalComponentElement(
        evalProps: EvalElementProps
      ): React.ReactElement<
        EvalElementProps,
        string | React.JSXElementConstructor<any>
      > | null {
        const {element: originalElement, env} = evalProps;
        const {currentComponent, seenComponents, ix3PotentialTargets} = env;
        const pageId = usePageId();
        const variantState = useVariantState();

        const symbolAtom = Plugins.getAtoms(env.plugins).get(symbolElementType);
        if (!symbolAtom) throw new Error('Symbol atom not found');

        const {
          element,
          environmentContext,
          sharedContext,
          plugins,
          components,
          componentContext,
          nativeIdPath,
          evalEnv,
          pluginsContext,
          trackingContext,
        } = useDynamo(originalElement, env, symbolAtom);

        const elementType = element.type;
        const component = components.get(elementType);

        if (!component) return null;

        // Avoid infinite loops
        if (seenComponents.has(elementType)) {
          console.error(
            `Recursive symbols are not supported:`,
            toHumanReadableString(elementType)
          );
          return null;
        }

        const envForData = {
          topEvalEnv: env.topEvalEnv,
          evalEnv,
          componentContext,
          components,
          plugins,
          sharedContext,
          environmentContext,
          pluginsContext,
          isComponentRootElement: false,
          ix3PotentialTargets: emptyArray,
          currentComponent,
          seenComponents,
          nativeIdPath,
          trackingContext,
        };

        const nextNativeIdPath = append(
          nativeIdPath,
          computeNativeId(element.id, environmentContext)
        );

        const dataType = getDataTypeFromElement(plugins)(components)(element);

        const isComponentInstanceInFocusedPath =
          isComponentInstanceInFocusedComponentPath(
            env.environmentContext,
            nextNativeIdPath
          );
        const currentlyFocusedComponentType = environmentContext.get(
          'focusedComponentType'
        );
        // We are evaluating the currently focused component if the instance is in focused path
        // and the focused component type matches the currently evaluated component
        const isEvaluatingFocusedComponent =
          isComponentInstanceInFocusedPath &&
          originalElement.type === currentlyFocusedComponentType;

        // If the component is in the focused component path, we want to use the default values.
        // Only exception for this is style variants, which are always applied.
        const elementDataToRender = isComponentInstanceInFocusedPath
          ? pruneElementDataToStyleVariants(
              dataType,
              Expressions.getElementData(element),
              isEvaluatingFocusedComponent,
              variantState,
              component
            )
          : element.data;

        const defaultData = Component.getDefaultData(component);
        const evaluatedDefaultData = defaultData
          ? evaluateComponentDefaults(plugins)(componentContext)(defaultData)
          : null;
        const data = handleOverrides(
          plugins,
          evalExpr(elementDataToRender)(envForData),
          dataType,
          evaluatedDefaultData,
          components
        );

        const expr = Component.getRender(component);
        const inPage = currentComponent
          ? namesEqual(currentComponent, pageComponentType)
          : false;
        const componentId = inPage
          ? pageId
          : currentComponent
            ? getName<string>(currentComponent)
            : null;

        const newEnv = {
          topEvalEnv: env.topEvalEnv,
          evalEnv: mergeDataToEnv(env.topEvalEnv)(data),
          componentContext,
          components,
          plugins,
          sharedContext,
          environmentContext,
          pluginsContext,
          isComponentRootElement: true,
          ix3PotentialTargets: componentId
            ? append(
                ix3PotentialTargets,
                pair(pair(componentId, element.id), nativeIdPath)
              )
            : ix3PotentialTargets,
          currentComponent: elementType,
          seenComponents: seenComponents.set(elementType, true),
          nativeIdPath: nextNativeIdPath,
          trackingContext: componentId
            ? append(env.trackingContext, {
                componentId:
                  getPluginName(elementType) === '__SitePlugin'
                    ? getName(elementType)
                    : componentId,
                instanceId: element.id,
                type: 'component',
              })
            : env.trackingContext,
        };

        // @ts-expect-error - This can technically return absolutely anything
        return evalExpr(expr)(newEnv);
      }, componentEqualProps(runtimeEnvironment))
  );

  const EvalComponentElement = function EvalComponentElement(
    props: EvalElementProps
  ): null | React.ReactElement<unknown> {
    const runtimeEnvironment = useRuntimeEnvironment();

    const InternalEvalComponentElement =
      internalEvalComponentElementFactory(runtimeEnvironment);

    return <InternalEvalComponentElement {...props} />;
  };

  const EvalAtomElement = React.memo(function EvalAtomElement({
    element: originalElement,
    env,
  }: EvalElementProps): null | React.ReactElement<unknown> {
    const pageId = usePageId();
    const collectionIdToPageIdMap = useCollectionIdToPageIdMap();
    const {currentComponent, ix3PotentialTargets, seenComponents} = env;
    const inPage = currentComponent
      ? namesEqual(currentComponent, pageComponentType)
      : false;
    const lastSeenComponentType = getLastSeenComponentType(currentComponent);
    const runtimeEnvironment = useRuntimeEnvironment();

    const atoms = Plugins.getAtoms(env.plugins);
    const atom = atoms.get(originalElement.type) || atoms.get(blockElementType);
    if (!atom) throw new Error('Block atom not found');

    const {
      element,
      environmentContext,
      sharedContext,
      plugins,
      components,
      componentContext,
      nativeIdPath,
      isComponentRootElement,
      evalEnv,
      pluginsContext,
      dynamoAttributes,
      dynamoClasses,
      dynamoBindingProps,
      legacyDynamoBindingPaths,
      trackingContext,
    } = useDynamo(originalElement, env, atom);

    const envForData = {
      topEvalEnv: env.topEvalEnv,
      evalEnv,
      componentContext,
      components,
      plugins,
      sharedContext,
      environmentContext,
      pluginsContext,
      isComponentRootElement: false,
      ix3PotentialTargets: emptyArray,
      currentComponent,
      seenComponents,
      nativeIdPath,
      trackingContext,
    };

    const envForAtomData = transformDataEnv(element.type, envForData);
    const data = evalExpr(element.data)(envForAtomData);

    const activeVariants = useActiveVariants(
      components,
      currentComponent,
      evalEnv,
      environmentContext,
      nativeIdPath
    );

    const styleBlockAttributes = useStyleBlockAttributes(
      element,
      data,
      activeVariants
    );

    const slugMap = useSlugMap();
    const variableCollections = useCssVariableCollectionsFromRenderContext();
    const ix3ElementTargets = useIx3ElementTargets();

    const styleBlockClasses =
      typeof styleBlockAttributes === 'string'
        ? styleBlockAttributes
        : styleBlockAttributes.classes;

    const isPreviewMode = usePreviewMode();
    const omitHiddenElementsInDesigner = useOmitHiddenElements();
    if (
      !isVisible(data) &&
      !isKeptInHtmlWhenHidden(data) &&
      (runtimeEnvironment !== WebflowEnvironments.DESIGNER ||
        isPreviewMode ||
        omitHiddenElementsInDesigner)
    )
      return null;

    const componentId = inPage
      ? pageId
      : currentComponent
        ? getName<string>(currentComponent)
        : null;

    let attributes: AttributesMap = createBaseAttributeMap(
      componentId,
      element.id,
      data,
      activeVariants,
      ix3PotentialTargets,
      nativeIdPath,
      ix3ElementTargets
    );

    if (typeof styleBlockAttributes !== 'string') {
      attributes = attributes.merge(styleBlockAttributes.attributes);
    }

    if (isComponentRootElement && KeySet.size(activeVariants)) {
      const component = currentComponent && components.get(currentComponent);

      if (component) {
        attributes = attributes.merge(
          createComponentVariantsAttributeMap(slugMap)(component)(
            activeVariants
          )
        );
      }
    }

    {
      // Note: `variableModes` is expected to be `Map<collectionId, modeId>`.
      //
      // These are currently only used inside user component definitions,
      // derived from simple `ESelect` bindings on the values of the map.
      const variableModes =
        data && typeof data === 'object' && 'variableModes' in data
          ? data.variableModes
          : null;

      if (variableModes && typeof variableModes === 'object') {
        const modeAttrs = resolveVariableModeAttributes(
          variableModes as Record<string, unknown>,
          variableCollections,
          slugMap
        );
        if (Object.keys(modeAttrs).length > 0) {
          attributes = attributes.merge(modeAttrs);
        }
      }
    }

    const classes: ClassesList = createBaseClassList(
      componentId,
      element,
      data,
      styleBlockClasses,
      activeVariants
    );

    const mergedAttributes = dynamoAttributes
      ? attributes.merge(dynamoAttributes)
      : attributes;
    const ix3ReifiedTargets = getIx3ElementTargets(ix3ElementTargets);

    const componentRootIdToApply = isComponentRootElement
      ? getComponentRootIdForAttribute(
          trackingContext,
          pageId,
          ix3ReifiedTargets
        )
      : null;

    const props: ExpansionProps = {
      evalExpr,
      fullEnv: envForAtomData,
      attributes:
        componentRootIdToApply != null
          ? mergedAttributes
              .set(COMPONENT_ID_ATTR, componentRootIdToApply)
              .set(VARIANT_STATE_ATTR, getActiveVariantOptionId(activeVariants))
          : mergedAttributes,
      classes: dynamoClasses ? classes.concat<string>(dynamoClasses) : classes,
      plugins,
      components,
      env: evalEnv,
      componentContext,
      isComponentRootElement,

      seenComponents: lastSeenComponentType,
      nativeIdPath,
      trackingContext,
      sharedContext,
      environmentContext,
      pluginsContext,
      style: dynamoBindingProps?.style || emptyObject,
      element,
      legacyDynamoBindingPaths,
      data,
    };

    if (dynamoBindingProps) {
      props.innerHTML = dynamoBindingProps.innerHTML;
      props.href = dynamoBindingProps.href;
      props.dataWHref = dynamoBindingProps.dataWHref;
      props.src = dynamoBindingProps.src;
      props.alt = dynamoBindingProps.alt;
      props.media = dynamoBindingProps.media;
      props.skuId = dynamoBindingProps.skuId;
      props.productId = dynamoBindingProps.productId;
      props.optionSets = dynamoBindingProps.optionSets;
      props.optionValues = dynamoBindingProps.optionValues;
      props.token = dynamoBindingProps.token;
      props.inventory = dynamoBindingProps.inventory;
      props.repeater = dynamoBindingProps.repeater;
    }

    const transformedProps = transformExpansionProps(
      props,
      collectionIdToPageIdMap,
      runtimeEnvironment
    );

    return React.createElement(expand(atom), transformedProps);
  }, commonEqualProps);

  type EvalElementType = React.ReactElement<
    EvalElementProps,
    React.ComponentType<EvalElementProps>
  >;

  const getEvalElement = (thing: EvaluatedData): null | EvalElementType => {
    // We are checking if the shape looks like a React element and the component
    // is EvalElement
    if (
      thing &&
      typeof thing === 'object' &&
      `$$typeof` in thing &&
      thing.$$typeof === REACT_ELEMENT_TYPE &&
      `type` in thing &&
      (thing.type === EvalComponentElement || thing.type === EvalAtomElement)
    )
      // @ts-expect-error - We assume props have the right shape
      return thing;

    return null;
  };

  const Render: React.ComponentType<{
    componentName?: Qualified<Component.ComponentName>;
    data?: EvaluatedData;
    components?: Component.ComponentMap;
    plugins: Plugins.PluginsType;
    evalEnv?: EvalEnv.EvalEnvType;
    componentContext?: PluginComponentContextType;
    sharedContext?: SharedContextType;
    environmentContext?: EnvironmentContextType;
    pluginsContext?: PluginsContext;
    renderContext?: RenderContext;
  }> = ({
    componentName = pageComponentType,
    data = emptyObject,
    components = QualifiedMap.empty(),
    plugins,
    evalEnv = Plugins.getEvalEnv(plugins),
    componentContext = emptyObject,
    sharedContext = SharedContext(),
    environmentContext = IMap({}),
    pluginsContext = IMap({}),
    renderContext = defaultRenderContext,
  }): React.ReactElement<unknown> => {
    if (process.env.NODE_ENV === 'development') {
      // Redefining every time in dev because dead code elim deletes everything in this block.
      const LEGACY_ENVIRONMENT_CONTEXT_KEYS = new Set([
        // Designer
        'focusedComponentType',
        'focusedSymbolNativeIdPath',
        'affectedNodeIds',
        'designSiteMode',
        'editSiteMode',
        'buildMode',
        'dropTargets',
        'editTargetNativeIdPath',
        'selectedNativeIdPath',
        'stopEditingText',
        'pseudoMode',
        'customNodeState',
        'editingGrid',
        'ix2Targets',
        'loadedHeadScripts',
        'bodyEventHandlers',
        'canNavigateFromPageToCMS',
        'isEditingIx2ActionList',
        'saveCollectionItemField',
        'contentEditorValidationError',
        'customCodeExecutionAllowed',
        'workflow',
        // Server
        'nowUtcString',
        'stripCustomCodeEmbedsForDebugging',
        'ix2StyleMap',
        // Data Manager
        'startEditingText',
        'classPrefix',
        'highlighted',
        'dataAutomationId',
        'disabled',
        // AIDA
        'generateShortId',
      ]);
      /**
       * Environment Context is deprecated. Since it isn't typed we have to do runtime verification
       * to inform consumers.
       */
      environmentContext.keySeq().forEach((key: string) => {
        if (!LEGACY_ENVIRONMENT_CONTEXT_KEYS.has(key)) {
          throw new Error(
            `Found key: ${key} in the environmentContext object. No new fields are allowed in the environmentContext object! Please use the new DesignerContext object inside of RenderContext instead. See: https://github.com/webflow/webflow/tree/dev/packages/systems/render`
          );
        }
      });
    }

    const _environmentContext =
      renderContext.environment.environment === 'Designer'
        ? setEnvironmentContextVariantState(environmentContext)(
            renderContext.environment.variantState
          )
        : environmentContext;

    // Build the top-level EvalEnv: merge component context into the base env,
    // then override dynamoRichTextToRichTextChildren so CMS rich text values
    // can be converted into renderable NodePojos using the now-available
    // components and plugins.
    const baseTopEvalEnv =
      mergeComponentContextToEnv(componentContext)(evalEnv);
    const topEvalEnv = useDynamoRichTextToRichTextChildren(
      baseTopEvalEnv,
      components,
      plugins,
      (expr) =>
        evalExpr(expr)({
          topEvalEnv,
          evalEnv: topEvalEnv,
          componentContext,
          components,
          plugins,
          sharedContext,
          environmentContext: _environmentContext,
          pluginsContext,
          isComponentRootElement: false,
          ix3PotentialTargets: emptyArray,
          currentComponent: null,
          seenComponents: QualifiedMap.empty(),
          nativeIdPath: emptyArray,
          trackingContext: emptyArray,
          __HACK__isDynamoRichText: true,
        })
    );

    const component = components.get(componentName);

    if (!component) return <></>;

    const expr = Component.getRender(component);

    const env: Env = {
      topEvalEnv,
      evalEnv: mergeDataToEnv(topEvalEnv)(data),
      componentContext,
      components,
      plugins,
      sharedContext,
      environmentContext: _environmentContext,
      pluginsContext,
      isComponentRootElement: false,
      ix3PotentialTargets: emptyArray,
      currentComponent: componentName,
      seenComponents: QualifiedMap.empty(),
      nativeIdPath: emptyArray,
      trackingContext: emptyArray,
    };

    const els = evalExpr(expr)(env);

    return (
      <RenderContextProvider value={renderContext}>
        {els as React.ReactNode}
      </RenderContextProvider>
    );
  };

  return Render;
};

export const forTestSuite = {
  hasNonEmptyLocalStyles,
};
